import { useQuery } from '@apollo/client';
import {
  ADMIN_MRR_SUMMARY,
  ADMIN_MRR_SERIES,
  ADMIN_SIGNUP_SERIES,
  ADMIN_SIGNUPS_BY_TIER_IN_RANGE,
} from '../../../graphql/adminQueries';
import { suggestForecastParamsFromActuals, buildSyncedActuals } from '../model/calibrate';

/** Cohort display allowlist — not FORECAST_PLAN_KEYS (that omits free/unknown). */
const SIGNUP_TIER_SLUG_ALLOWLIST = new Set([
  'free',
  'basic',
  'premium',
  'enterprise',
  'unknown',
]);

const SIGNUP_TIER_PREVIEW_ORDER = [
  'free',
  'basic',
  'premium',
  'enterprise',
  'unknown',
  'other',
];

const SIGNUP_TIER_PREVIEW_LABELS = new Map([
  ['free', 'free'],
  ['basic', 'basic'],
  ['premium', 'premium'],
  ['enterprise', 'enterprise'],
  ['unknown', 'Unknown'],
  ['other', 'other'],
]);

/**
 * Collapse a raw API slug onto the locked allowlist before any object key.
 * Extra / prototype-polluting slugs become `other`.
 * @param {unknown} rawSlug
 * @returns {'free'|'basic'|'premium'|'enterprise'|'unknown'|'other'}
 */
export function collapseSignupTierSlug(rawSlug) {
  const slug = typeof rawSlug === 'string' && rawSlug.length > 0 ? rawSlug : 'unknown';
  if (SIGNUP_TIER_SLUG_ALLOWLIST.has(slug)) return slug;
  return 'other';
}

/**
 * @param {Array<{ slug?: string, count?: number }>|null|undefined} rows
 * @returns {Array<{ slug: string, count: number }>}
 */
export function collapseSignupTierMix(rows) {
  const counts = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const slug = collapseSignupTierSlug(row?.slug);
    const n = Number(row?.count);
    const count = Number.isFinite(n) ? n : 0;
    counts.set(slug, (counts.get(slug) || 0) + count);
  }
  return SIGNUP_TIER_PREVIEW_ORDER.filter((slug) => counts.has(slug)).map((slug) => ({
    slug,
    count: counts.get(slug),
  }));
}

/**
 * Thin "Calibrate from live" hook — fetches admin aggregates and returns suggestions.
 */
export function useCalibrateFromActuals() {
  const { data: mrrData, loading: mrrLoading, error: mrrError } = useQuery(ADMIN_MRR_SUMMARY);
  const {
    data: seriesData,
    loading: seriesLoading,
    error: seriesError,
  } = useQuery(ADMIN_MRR_SERIES, {
    variables: {
      range: {
        from: daysAgoIso(180),
        to: daysAgoIso(0),
      },
    },
  });
  const {
    data: signupData,
    loading: signupLoading,
    error: signupError,
  } = useQuery(ADMIN_SIGNUP_SERIES, {
    variables: {
      range: {
        from: daysAgoIso(27),
        to: daysAgoIso(0),
      },
    },
  });

  const mrr = mrrData?.adminMrrSummary;
  const signupSeries = signupData?.adminSignupSeries;
  const mrrSeriesRaw = seriesData?.adminMrrSeries;

  const suggestions = suggestForecastParamsFromActuals({
    mrrCents: mrr?.mrrCents,
    activeSubscriptions: mrr?.activeSubscriptions,
    signupSeries,
  });

  const syncedActuals = buildSyncedActuals({
    mrrSeries: Array.isArray(mrrSeriesRaw)
      ? mrrSeriesRaw
      : mrr
        ? [{ asOf: mrr.asOf, mrrCents: mrr.mrrCents, activeSubscriptions: mrr.activeSubscriptions }]
        : [],
    asOf: mrr?.asOf,
  });

  return {
    suggestions,
    syncedActuals,
    loading: mrrLoading || signupLoading || seriesLoading,
    error: mrrError || signupError || seriesError,
    asOf: mrr?.asOf,
  };
}

function daysAgoIso(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function SignupMixPreview() {
  const { data, error, loading } = useQuery(ADMIN_SIGNUPS_BY_TIER_IN_RANGE, {
    variables: {
      range: {
        from: daysAgoIso(27),
        to: daysAgoIso(0),
      },
    },
  });

  if (loading || error) return null;

  const mix = collapseSignupTierMix(data?.adminSignupsByTierInRange);
  if (!mix.length) return null;

  return (
    <section className="chart-card economics-panel">
      <h2>Acquisition mix (tier at signup)</h2>
      <p className="chart-hint">
        Last 28 UTC days — not the Growth range control. Includes unverified and deactivated
        accounts; not paying logos.
      </p>
      <ul>
        {mix.map((row) => (
          <li key={row.slug}>
            {SIGNUP_TIER_PREVIEW_LABELS.get(row.slug)} {row.count}
          </li>
        ))}
      </ul>
    </section>
  );
}

function CalibrateBody({ onApply }) {
  const { suggestions, syncedActuals, loading, error, asOf } = useCalibrateFromActuals();

  if (loading) {
    return <p className="chart-hint">Loading live calibration inputs…</p>;
  }

  if (error) {
    return (
      <p className="module-status">
        Could not load live calibration data. Deploy admin analytics queries on the API.
      </p>
    );
  }

  if (!suggestions) {
    return <p className="chart-hint">No live MRR or signup data available yet.</p>;
  }

  return (
    <section className="chart-card economics-panel">
      <h2>Calibrate from live</h2>
      <p className="chart-hint">
        Suggested opening params from Stripe cache + signup rollups
        {asOf ? ` · MRR as of ${new Date(asOf).toLocaleString()}` : ''}
        {syncedActuals?.mrrSeries?.length
          ? ` · ${syncedActuals.mrrSeries.length} trailing MRR points for Results overlay`
          : ''}
      </p>
      <div className="kpi-grid">
        {suggestions.openingMrr != null && (
          <article className="kpi-card">
            <p className="kpi-label">Opening MRR</p>
            <p className="kpi-value">${suggestions.openingMrr.toLocaleString()}</p>
          </article>
        )}
        {suggestions.logos != null && (
          <article className="kpi-card">
            <p className="kpi-label">Paid logos</p>
            <p className="kpi-value">{suggestions.logos}</p>
          </article>
        )}
        {suggestions.signupRatePerMonth != null && (
          <article className="kpi-card">
            <p className="kpi-label">Signup rate / mo</p>
            <p className="kpi-value">{suggestions.signupRatePerMonth}</p>
          </article>
        )}
      </div>
      {onApply && (
        <div className="economics-preset-row">
          <button
            type="button"
            className="primary-button"
            onClick={() => onApply({ ...suggestions, syncedActuals })}
          >
            Apply available calibration
          </button>
        </div>
      )}
      <p className="module-footnote">
        Applies only available opening MRR/logos and organic signup rate; missing actuals remain
        unchanged. Trailing Stripe MRR is stored as <code>syncedActuals</code> for plan-vs-actuals
        overlay on Results.
      </p>
    </section>
  );
}

/**
 * Mix preview is an always-mounted sibling so CalibrateBody early-returns
 * cannot unmount its query or hide Apply when mix fails.
 * @param {{ onApply?: (payload: object) => void }} props
 */
export default function CalibrateFromActuals({ onApply }) {
  return (
    <>
      <CalibrateBody onApply={onApply} />
      <SignupMixPreview />
    </>
  );
}
